import React, { FC, ReactNode, useEffect, useRef, useState } from 'react'
import Head from 'next/head'
import MobileConnector, { ConnectorProps as MobileConnectorProps } from 'connectors/Screen'
import polaroidStyles from './Polaroid.module.scss'
import {
  ARTWORK_STATUS,
  getArtworkInfo,
  getCleanCategory,
  getImageAltText,
  getImageSrc,
  getPrintAltText,
  isCategorySculpture,
} from './helpers'
import { SAArtwork } from 'types/Artwork'
import PolaroidActions from './Actions/PolaroidActions'
import classNames from 'classnames'
import { convertFromCentsToDollars } from 'components/FormatPrice/helpers'
import { LocaleCurrency } from 'components/FormatPrice'
import SALink from 'components/SALink'
import { DimensionsUnit } from 'components/MeasurementUnit'
import useMasonry from './useMasonry'
import { usePolaroidEvents } from './usePolaroidEvents'
import PriceStatusRow from './components/PriceStatusRow'
import { PolaroidSkeleton } from './components/PolaroidSkeleton'
import { addToCartGAWhitelist } from './Actions/useAddToCart'
import { capitalizeString } from 'lib/helpers'
import useQA from 'hooks/useQA'

export type PolaroidSpecificPageProps = {
  algoliaIndice?: string
  algoliaUserToken?: string
  dataApi?: string
  dataList?:
    | 'plp-results'
    | 'feature-results'
    | 'favorites-results'
    | 'artist-profile-all-artworks'
    | 'curated-deals-artworks'
  dataListId?: string
  dataListName?: string
}

type PolaroidProps = MobileConnectorProps &
  PolaroidSpecificPageProps & {
    artwork: SAArtwork
    priority?: boolean
    lazyLoad?: boolean
    isImageSrcFromProd?: boolean
    customStyle?: string
    isArtistNameVisible?: boolean
    isPrintsFromVisible?: boolean
    userID?: number
    isUsersArtworksAccount?: boolean
    isMediumMaterialVisible?: boolean
    isDimensionsVisible?: boolean
    showPrintsFromInPrice?: boolean
    showOriginalPrice?: boolean
    customPriceElement?: ReactNode
    customPromoCode?: string
    hasPrints?: boolean
    isPrintForDataLayerEvent?: boolean
  }

const Polaroid: FC<PolaroidProps> = ({
  isMobile: isMobileTraffic,
  screenWidth,
  artwork,
  priority,
  lazyLoad,
  isImageSrcFromProd,
  algoliaIndice,
  algoliaUserToken,
  dataApi = '',
  dataList = '',
  dataListId = '',
  dataListName = '',
  customStyle = '',
  isArtistNameVisible = true,
  isPrintsFromVisible = true,
  userID = 0,
  isUsersArtworksAccount,
  isMediumMaterialVisible = true,
  isDimensionsVisible = true,
  showPrintsFromInPrice,
  showOriginalPrice,
  customPriceElement,
  customPromoCode,
  hasPrints,
  isPrintForDataLayerEvent,
}) => {
  const { testId } = useQA()
  const {
    artworkId,
    artworkTitle,
    aspectRatio,
    category,
    positionID,
    depth,
    hasPrint,
    hasOpenEditions,
    height,
    isReadyToHang: isReadyToHangNumber,
    listPrice,
    materials,
    mediums,
    minPrintPrice,
    profileUrl,
    originalStatus,
    sku,
    styles,
    subject,
    width,
  } = artwork
  const capitalizedArtworkTitle = capitalizeString(artworkTitle)
  const isSculpture = isCategorySculpture(category)
  const cleanCategory = getCleanCategory(category)
  const capitalizedCleanCategory = capitalizeString(cleanCategory)

  const [imageDimensions, setImageDimensions] = useState<{
    width: number | undefined
    height: number | undefined
  }>({
    width: 200, // just a default value to core web vitals
    height: parseInt((200 / aspectRatio).toString(), 10), // just a default value to core web vitals
  })
  const figureRef = useRef<HTMLElement | null>(null)
  const [isSkeletonVisible, setIsSkeletonVisible] = useState(true)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const isAvailable = originalStatus === ARTWORK_STATUS.AVAILABLE
  const isSold = originalStatus === ARTWORK_STATUS.SOLD
  const isUnavailable = originalStatus === ARTWORK_STATUS.UNAVAILABLE
  const isReserved = originalStatus === ARTWORK_STATUS.RESERVED

  const { artistName, country, dataPosition, href, src } = getArtworkInfo({
    artwork,
    hasPrints: hasPrints || (isSold && hasOpenEditions),
  })
  const imageSrc = getImageSrc({
    originalSrc: src,
    priority: isImageSrcFromProd,
  })
  const capitalizedArtistName = capitalizeString(artistName)

  const getFigureDimensions = () => {
    if (figureRef.current) {
      const { width } = figureRef.current.getBoundingClientRect()
      const height = parseInt((width / aspectRatio).toString(), 10)
      const roundedWidth = parseInt(width.toString(), 10)
      setImageDimensions({ width: roundedWidth, height })
    }
  }

  const handleLoadImage = () => {
    setIsSkeletonVisible(false)
    getFigureDimensions()
  }

  // Trigger all Google Analytics events
  const { handleArtworkClick } = usePolaroidEvents({
    imageRef,
    artworkId,
    artworkTitle: capitalizedArtworkTitle,
    artistName: capitalizedArtistName,
    positionID,
    category,
    listPrice,
    sku,
    algoliaIndice,
    algoliaUserToken,
    dataPosition,
    dataListId,
    dataListName,
    userID,
    originalStatus,
    isPrintForDataLayerEvent,
  })

  const altParams = {
    artistName: capitalizedArtistName,
    subject,
    styles,
    category,
  }
  const productEvent = addToCartGAWhitelist({
    artistName: capitalizedArtistName,
    category,
    listId: dataList,
    price: listPrice,
    sku,
    title: capitalizedArtworkTitle,
    dataPosition,
    artworkId,
  })

  const isReadyToHang = isReadyToHangNumber === 1
  const shouldOpenNewDesktopPdpTab = !isMobileTraffic
  const shouldMediumMaterialAndDimensionsRender =
    !((isSold || isUnavailable) && hasOpenEditions) &&
    (isMediumMaterialVisible || isDimensionsVisible)
  const shouldPrintsFromRender =
    isPrintsFromVisible && minPrintPrice > 0 && ((isAvailable && hasOpenEditions) || isReserved)
  const shoulReadyToHangRender = isReadyToHang && (isAvailable || isReserved)

  // number of elements in Polaroid to calculate masonry
  const infoSpanConditions = [
    true, // image
    true, // price or status
    true, // artwork title
    isArtistNameVisible, // artist name
    shouldMediumMaterialAndDimensionsRender, // medium/material and dimensions
    showOriginalPrice, // Original Price
    shouldPrintsFromRender, // Prints From
    shoulReadyToHangRender, // ready to hang
  ]
  const infoSpanCount = infoSpanConditions.filter((value) => value === true).length
  const { totalSpanCount } = useMasonry({ imageDimensions, aspectRatio, infoSpanCount })

  useEffect(() => {
    getFigureDimensions()
    window.addEventListener('resize', getFigureDimensions)

    // To calculate width and heigh of images also if an element in the page changes the width of the <figure> (because now figure is 1fr using grid system)
    const resizeObserver = new ResizeObserver(() => {
      getFigureDimensions()
    })

    if (figureRef.current) {
      resizeObserver.observe(figureRef.current)
    }

    return () => {
      window.removeEventListener('resize', getFigureDimensions)
      if (figureRef.current) {
        resizeObserver.unobserve(figureRef.current)
      }
    }
  }, [])

  return (
    <>
      {priority && (
        /* Preload Images */
        <Head>
          <link
            key={`__nimg-${imageSrc}`}
            rel='preload'
            fetchPriority='high'
            as='image'
            href={imageSrc}
          />
        </Head>
      )}
      <figure
        className={classNames(polaroidStyles.polaroidContainer, {
          [customStyle]: customStyle !== '',
        })}
        style={{
          gridRowEnd: `span ${totalSpanCount}`,
          paddingTop: `calc(calc(100% / ${aspectRatio}) + 8px)`, // 8px of spacing between image and text as it is an absolute position
        }}
        ref={figureRef}
      >
        <SALink
          aria-label={capitalizedArtworkTitle}
          data-api={dataApi}
          data-id={sku}
          data-name={capitalizedArtworkTitle}
          data-position={dataPosition}
          data-list={dataList}
          href={href}
          onClick={handleArtworkClick}
          rel={capitalizedArtworkTitle}
          target={shouldOpenNewDesktopPdpTab ? '_blank' : ''}
          title={`${capitalizedArtworkTitle} ${capitalizedCleanCategory}`}
          className={polaroidStyles.figureImageContainer}
        >
          <PolaroidSkeleton
            width={imageDimensions.width}
            height={imageDimensions.height}
            customClassName={classNames(polaroidStyles.skeletonVisible, {
              [polaroidStyles.skeletonInvisible]: !isSkeletonVisible,
            })}
          />
          <img
            {...(lazyLoad
              ? {
                  className: 'lazyload',
                  src: imageSrc,
                  loading: 'lazy',
                }
              : {
                  src: imageSrc,
                })}
            onLoad={handleLoadImage}
            className={classNames({
              [polaroidStyles.figureVisible]: !isSkeletonVisible,
              [polaroidStyles.figureInvisible]: isSkeletonVisible,
            })}
            ref={imageRef}
            alt={hasPrint ? getPrintAltText(altParams) : getImageAltText(altParams)}
            width={imageDimensions.width}
            height={imageDimensions.height}
          />
        </SALink>

        <div
          className={classNames(polaroidStyles.polaroidPriceAndActionsContainer, {
            [polaroidStyles.polaroidPriceAndActionsContainerPrice]:
              isAvailable ||
              ((isSold || isUnavailable) && hasOpenEditions) ||
              (isUnavailable && !hasOpenEditions),
          })}
        >
          {customPriceElement ? (
            customPriceElement
          ) : (
            <PriceStatusRow
              isSold={isSold}
              isReserved={isReserved}
              isUnavailable={isUnavailable}
              hasOpenEditions={hasOpenEditions}
              minPrintPrice={minPrintPrice}
              listPrice={listPrice}
              showPrintsFromInPrice={showPrintsFromInPrice}
            />
          )}
          <PolaroidActions
            screenWidth={screenWidth}
            artworkId={artworkId}
            isSold={isSold}
            sku={sku}
            status={originalStatus}
            price={listPrice}
            productEvent={productEvent}
            shouldShowEditIcon={isUsersArtworksAccount}
            customPromoCode={customPromoCode}
            hasPrints={hasPrints}
          />
        </div>

        <div className={polaroidStyles.polaroidInfoContainer}>
          <h6
            className={classNames(polaroidStyles.truncateTitleCase, 'defaultTypographyH6')}
            {...testId('artwork-title')}
          >
            <SALink
              className={classNames(
                polaroidStyles.linkTruncateText,
                polaroidStyles.textUnderlineOnHover
              )}
              alt={`${capitalizedArtworkTitle}, ${category}`}
              href={href}
              onClick={handleArtworkClick}
              rel='nofollow'
              title={`View ${capitalizedArtworkTitle} ${capitalizedCleanCategory}`}
              target={shouldOpenNewDesktopPdpTab ? '_blank' : ''}
            >
              {`${capitalizedArtworkTitle} ${capitalizedCleanCategory}`}
            </SALink>
          </h6>
          {isArtistNameVisible && (
            <h6
              className={classNames(polaroidStyles.truncateTitleCase, 'defaultTypographyXSmall')}
              {...testId('artwork-name')}
            >
              <SALink
                className={classNames(
                  polaroidStyles.linkTruncateText,
                  polaroidStyles.textUnderlineOnHover
                )}
                alt={profileUrl}
                href={profileUrl}
                rel='nofollow'
                title={`View artist ${capitalizedArtistName} profile`}
                {...testId('profile-url')}
              >
                {`${capitalizedArtistName}${country ? `, ${country}` : ''}`}
              </SALink>
            </h6>
          )}
          {shouldMediumMaterialAndDimensionsRender && (
            <div className={polaroidStyles.polaroidMaterialAndDimensionsContainer}>
              {isMediumMaterialVisible && (
                <>
                  <p className={polaroidStyles.textGunmetalOpacity} {...testId('medium-material')}>
                    {mediums.length ? `${mediums[0]} on ` : ''}
                    {`${materials.length && materials[0]}`}
                  </p>

                  <div className={polaroidStyles.customDot} />
                </>
              )}
              {isDimensionsVisible && (
                <p className={polaroidStyles.textGunmetalOpacity} {...testId('artwork-dimensions')}>
                  <DimensionsUnit
                    isDepthVisible={isSculpture}
                    dimensions={{
                      width,
                      height,
                      depth,
                    }}
                  />
                </p>
              )}
            </div>
          )}
          {/* // removing for now 08/01/2025 */}
          {/* {isSold && (
            <p
              className={polaroidStyles.textGunmetalOpacity}
                    {...testId('artwork-dimensions')}
              {...testId('original-price-isSold')}
            >
              Original Price <LocaleCurrency price={convertFromCentsToDollars(listPrice)} />
            </p>
          )} */}

          {showOriginalPrice && (
            <p className={polaroidStyles.textGunmetalOpacity} {...testId('original-price')}>
              Original <LocaleCurrency price={convertFromCentsToDollars(listPrice)} />
            </p>
          )}

          {shouldPrintsFromRender && (
            <p className={polaroidStyles.textGunmetalOpacity} {...testId('prints-price')}>
              Prints From <LocaleCurrency price={convertFromCentsToDollars(minPrintPrice)} />
            </p>
          )}
          {shoulReadyToHangRender && (
            <div>
              <span className={polaroidStyles.textGunmetalOpacity}>Ready to hang</span>
            </div>
          )}
        </div>
      </figure>
    </>
  )
}

export default MobileConnector(Polaroid)
